import { inject, Injectable, signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, Subscription, interval, of } from 'rxjs';
import { catchError, exhaustMap, finalize, map, switchMap, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';

interface ApiEnvelope<T> {
  success: boolean;
  statusCode: number;
  message: string;
  data: T;
}

const API_BASE = environment.apiBaseUrl;

/** Reserved types (TASK_DUE/TASK_OVERDUE/APPOINTMENT_REMINDER/KYC_REFRESH) are not
 *  yet emitted by any backend trigger — see normax-crm-backend's changelog. */
export type NotificationType =
  | 'TASK_DUE'
  | 'TASK_OVERDUE'
  | 'APPOINTMENT_REMINDER'
  | 'KYC_REFRESH'
  | 'COMPLIANCE_ALERT'
  | 'SYSTEM';

export interface AppNotification {
  id: string;
  type: NotificationType;
  title: string;
  body: string;
  link: string | null;
  readAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Paginated<T> {
  items: T[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface NotificationFilters {
  page?: number;
  limit?: number;
  read?: boolean;
  type?: NotificationType;
}

const DEFAULT_POLL_MS = 45_000;
const RECENT_LIMIT = 8;

/**
 * Data-access + live state for `/v1/notifications`. Exposes signals
 * (`unreadCount`, `recent`) the bell badge/dropdown read directly, and a
 * polling loop that keeps `unreadCount` current. Polling is intentionally the
 * only transport detail here — swapping to SSE/WS later only touches this
 * service's internals (`startPolling`/`stopPolling`), not its callers.
 *
 * Error-handling contract: every method here that a caller might reasonably
 * fire-and-forget (`refreshUnreadCount`, `loadRecent`, and the follow-up
 * count refresh inside `markRead`/`bulkRead`/`remove`/`bulkDelete`) catches
 * its own errors internally and never errors its returned Observable — a
 * transient 401/500/network failure degrades to "keep the last known state"
 * instead of surfacing as an unhandled error. The *primary* mutation in
 * `markRead`/`markAllRead`/`bulkRead`/`remove`/`bulkDelete` still propagates
 * its own errors normally, so callers that want to show "failed to X" (e.g.
 * the all-notifications page) still can.
 */
@Injectable({ providedIn: 'root' })
export class NotificationsService {
  private readonly http = inject(HttpClient);

  private readonly unreadCountSignal = signal(0);
  readonly unreadCount = this.unreadCountSignal.asReadonly();

  private readonly recentSignal = signal<AppNotification[]>([]);
  readonly recent = this.recentSignal.asReadonly();

  private readonly loadingRecentSignal = signal(false);
  readonly loadingRecent = this.loadingRecentSignal.asReadonly();

  private pollSubscription: Subscription | null = null;

  list(filters: NotificationFilters = {}): Observable<Paginated<AppNotification>> {
    return this.http
      .get<ApiEnvelope<Paginated<AppNotification>>>(`${API_BASE}/notifications`, {
        params: this.toParams(filters as Record<string, unknown>),
      })
      .pipe(map((res) => res.data));
  }

  /**
   * Refreshes the unread-count signal from the server (the bell badge's
   * source of truth). Never errors — a failed refresh just keeps the last
   * known count, since this is called on a timer and from fire-and-forget
   * follow-ups where a transient failure shouldn't be visible to the user.
   */
  refreshUnreadCount(): Observable<{ count: number }> {
    return this.http
      .get<ApiEnvelope<{ count: number }>>(`${API_BASE}/notifications/unread-count`)
      .pipe(
        map((res) => res.data),
        tap((res) => this.unreadCountSignal.set(res.count)),
        catchError(() => of({ count: this.unreadCountSignal() })),
      );
  }

  /**
   * Loads the latest notifications into the `recent` signal (the bell
   * dropdown). Never errors — on failure `recent` simply keeps its last
   * known contents rather than the dropdown erroring out.
   */
  loadRecent(limit = RECENT_LIMIT): Observable<Paginated<AppNotification>> {
    this.loadingRecentSignal.set(true);
    return this.list({ page: 1, limit }).pipe(
      tap((res) => this.recentSignal.set(res.items)),
      catchError(() =>
        of<Paginated<AppNotification>>({ items: this.recentSignal(), page: 1, limit, total: 0, totalPages: 0 }),
      ),
      finalize(() => this.loadingRecentSignal.set(false)),
    );
  }

  /** Mark one notification read (idempotent). Patches `recent` locally and re-syncs the count. */
  markRead(id: string): Observable<AppNotification> {
    return this.http
      .patch<ApiEnvelope<AppNotification>>(`${API_BASE}/notifications/${id}/read`, {})
      .pipe(
        map((res) => res.data),
        tap((updated) => {
          this.recentSignal.update((items) =>
            items.map((n) => (n.id === id ? updated : n)),
          );
        }),
        switchMap((updated) => this.refreshUnreadCount().pipe(map(() => updated))),
      );
  }

  /** Mark every one of the caller's unread notifications read. */
  markAllRead(): Observable<{ message: string; count: number }> {
    return this.http
      .patch<ApiEnvelope<{ message: string; count: number }>>(
        `${API_BASE}/notifications/read-all`,
        {},
      )
      .pipe(
        map((res) => res.data),
        tap(() => {
          this.recentSignal.update((items) =>
            items.map((n) => (n.readAt ? n : { ...n, readAt: new Date().toISOString() })),
          );
          this.unreadCountSignal.set(0);
        }),
      );
  }

  bulkRead(ids: string[]): Observable<{ message: string; count: number }> {
    return this.http
      .patch<ApiEnvelope<{ message: string; count: number }>>(
        `${API_BASE}/notifications/bulk-read`,
        { ids },
      )
      .pipe(
        map((res) => res.data),
        tap(() => {
          const idSet = new Set(ids);
          this.recentSignal.update((items) =>
            items.map((n) => (idSet.has(n.id) && !n.readAt ? { ...n, readAt: new Date().toISOString() } : n)),
          );
        }),
        switchMap((result) => this.refreshUnreadCount().pipe(map(() => result))),
      );
  }

  remove(id: string): Observable<{ message: string }> {
    return this.http
      .delete<ApiEnvelope<{ message: string }>>(`${API_BASE}/notifications/${id}`)
      .pipe(
        map((res) => res.data),
        tap(() => {
          this.recentSignal.update((items) => items.filter((n) => n.id !== id));
        }),
        switchMap((result) => this.refreshUnreadCount().pipe(map(() => result))),
      );
  }

  bulkDelete(ids: string[]): Observable<{ message: string; count: number }> {
    return this.http
      .post<ApiEnvelope<{ message: string; count: number }>>(
        `${API_BASE}/notifications/bulk-delete`,
        { ids },
      )
      .pipe(
        map((res) => res.data),
        tap(() => {
          const idSet = new Set(ids);
          this.recentSignal.update((items) => items.filter((n) => !idSet.has(n.id)));
        }),
        switchMap((result) => this.refreshUnreadCount().pipe(map(() => result))),
      );
  }

  /**
   * Starts polling `unread-count`; safe to call repeatedly (no-op if already
   * polling). The first refresh fires immediately and synchronously (the
   * badge shouldn't wait a full interval on first render); recurring polls
   * run through `exhaustMap` so a slow request can never overlap with the
   * next tick — the next tick is simply skipped until the current one
   * settles. Both the immediate refresh and the recurring interval are added
   * to a single aggregate `Subscription` stored in `pollSubscription`, so
   * `stopPolling()`/`resetState()` cancels the immediate one too: left
   * untracked, that request could otherwise resolve *after* `resetState()`
   * and clobber `unreadCountSignal` back to a stale/cross-user value.
   */
  startPolling(intervalMs = DEFAULT_POLL_MS): void {
    if (this.pollSubscription) return;
    const subscription = new Subscription();
    subscription.add(this.refreshUnreadCount().subscribe());
    subscription.add(
      interval(intervalMs)
        .pipe(exhaustMap(() => this.refreshUnreadCount()))
        .subscribe(),
    );
    this.pollSubscription = subscription;
  }

  stopPolling(): void {
    this.pollSubscription?.unsubscribe();
    this.pollSubscription = null;
  }

  /**
   * Clears cached notification state and stops polling. Must be called on
   * logout/session-clear — otherwise the previous user's `unreadCount`/`recent`
   * signals (this service is `providedIn: 'root'`, so they outlive the
   * session) linger into the next login, and a failed initial request for the
   * new user would fall back to that stale, cross-user data via the
   * "keep last known state" error handling above.
   */
  resetState(): void {
    this.stopPolling();
    this.unreadCountSignal.set(0);
    this.recentSignal.set([]);
    this.loadingRecentSignal.set(false);
  }

  private toParams(filters: Record<string, unknown>): HttpParams {
    let params = new HttpParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, String(value));
      }
    }
    return params;
  }
}
